class MealItem {
  constructor({ handle, product, configProduct, duration, stepCategory }) {
    this.handle       = handle;
    this.product      = product;        // Shopify /products/{handle}.js response
    this.configProd   = configProduct;  // MEAL_CONFIG entry { handle, title?, nutrition }
    this.duration     = duration;       // current week count (1 | 2 | 4 | 8)
    this.stepCategory = stepCategory;   // e.g. "Breakfast", "Lunch"
    this.dietaryPref        = null;  // 'Veg' | 'Egg' | 'Non-Veg' | null
    this.selectedQuantity   = null;  // multiplier number, or null
    this.selectedSubProduct = null;  // handle string from subProducts, or null
    this.selectedDeliveryTime = null; // delivery_time key string, or null
  }

  // ─── Static helpers ─────────────────────────────────────────────────────────

  // Shopify INR stores in paise when price > ₹100 (i.e. raw value > 10000)
  static parsePaise(raw) {
    const p = parseFloat(raw);
    return isNaN(p) ? 0 : (p > 10000 ? p / 100 : p);
  }

  // ─── Variant resolution ──────────────────────────────────────────────────────
  // Pass 1 : category + duration match (highest specificity)
  // Pass 2 : duration match only, sorted by diet preference relevance
  // Pass 3 : variants[0] fallback
  _resolveVariant(duration, pref, category) {
    const variants = this.product?.variants;
    if (!variants?.length) return null;

    const cfg      = window.MEAL_CONFIG;
    const durLabel = (cfg?.durationMap?.[duration] || `${duration} Week`).toLowerCase();
    const cat      = (category || this.stepCategory || '').toLowerCase();
    const prefL    = (pref || '').toLowerCase();

    // Pass 1 — must match both category and duration
    let best = null, bestScore = -1;
    variants.forEach(v => {
      const t = v.title.toLowerCase();
      if (!t.includes(cat) || !t.includes(durLabel)) return;
      const score = 3 + (prefL && t.includes(prefL) ? 4 : 0);
      if (score > bestScore) { best = v; bestScore = score; }
    });
    if (best) return best;

    // Pass 2 — duration match only, prefer diet-matching variant
    const byDur = variants
      .filter(v => v.title.toLowerCase().includes(durLabel))
      .sort((a, b) => {
        const sA = (prefL && a.title.toLowerCase().includes(prefL)) ? 1 : 0;
        const sB = (prefL && b.title.toLowerCase().includes(prefL)) ? 1 : 0;
        return sB - sA;
      });
    if (byDur.length) return byDur[0];

    // Pass 3 — first variant
    return variants[0];
  }

  // ─── Public getters ──────────────────────────────────────────────────────────

  getVariant(duration = this.duration) {
    return this._resolveVariant(duration, this.dietaryPref, this.stepCategory);
  }

  getPrice(duration = this.duration) {
    const v = this.getVariant(duration);
    const base = v
      ? MealItem.parsePaise(v.price)
      : MealItem.parsePaise(this.product?.variants?.[0]?.price);
    const qty = this.selectedQuantity || 1;
    return base * qty;
  }

  // Always resolves against the 1-week variant for the card price label
  // Savings for a given duration — reads from MPB_METAFIELDS[handle].variants[id].savings
  // Mirrors nutripack.liquid's retentionData.savings * p.multiplier pattern
  getSavings(duration = this.duration) {
    if (duration <= 1) return 0;
    const v = this.getVariant(duration);
    if (!v) return 0;

    const qty = this.selectedQuantity || 1;

    // Prefer metafield savings (nutripack pattern) — multiply by quantity multiplier
    const variantMeta = window.MPB_METAFIELDS?.[this.handle]?.variants?.[v.id];
    if (variantMeta?.savings) return parseInt(variantMeta.savings) * qty;

    // Fallback: price difference between (1-week × N) and the N-week variant price, scaled by qty
    const weeklyPrice  = this.getWeeklyPrice();
    const nWeekPrice   = MealItem.parsePaise(v.price);
    const diff         = Math.round(weeklyPrice * duration - nWeekPrice);
    return diff > 0 ? diff * qty : 0;
  }

  getWeeklyPrice() {
    const v1 =
      this._resolveVariant(1, this.dietaryPref, this.stepCategory) ||
      (this.product?.variants || []).find(v => v.title.toLowerCase().includes('1 week')) ||
      this.product?.variants?.[0] ||
      null;
    return v1 ? MealItem.parsePaise(v1.price) : 0;
  }

  // Returns the per_meal_price from product_details_json metafield, or null if absent
  getPerMealPrice() {
    const details = window.MPB_METAFIELDS?.[this.handle]?.metafields?.product_details;
    if (!details) return null;
    const raw = details.per_meal_price;
    if (raw === undefined || raw === null || raw === '') return null;
    const n = parseFloat(raw);
    return isNaN(n) ? null : n;
  }

  getNutrition() {
    return this.configProd?.nutrition || {};
  }

  // Returns true if this product has veg/egg/non-veg diet option values
  hasDietOptions() {
    const DIET_KEYWORDS = ['veg', 'egg', 'non-veg', 'nonveg', 'non veg', 'vegetarian'];
    return (this.product?.options || []).some(o =>
      (o.values || []).some(v => DIET_KEYWORDS.includes(v.trim().toLowerCase()))
    );
  }

  // ─── Mutators ────────────────────────────────────────────────────────────────

  setDuration(duration) {
    this.duration = duration;
  }

  // Calling with the same pref a second time toggles it off
  setDietaryPref(pref) {
    this.dietaryPref = (pref === this.dietaryPref) ? null : pref;
  }

  clearDietaryPref() {
    this.dietaryPref = null;
  }

  // Returns the retention_info object for this product, or null.
  // Mirrors nutripack.liquid's `{% assign retention = product.metafields.custom.retention_info.value %}`.
  // Priority: variant-level retention_info → product-level retention_info → null
  //
  // Robustness: Shopify can store metafields as JSON-type (already parsed) or as
  // single_line_text (comes through as a string). We JSON.parse strings so both
  // storage types work transparently.
  getRetentionInfo() {
    const meta = window.MPB_METAFIELDS?.[this.handle];
    if (!meta) return null;   // product not in Liquid handles list — silent

    const _parse = raw => {
      if (!raw) return null;
      if (typeof raw === 'string') {
        try { return JSON.parse(raw); } catch { return null; }
      }
      return raw;
    };

    const _valid = obj => obj && typeof obj === 'object' && Object.keys(obj).length > 0;

    // 1. Try variant-level (some products may set it per-variant)
    const v = this.getVariant();
    if (v) {
      const varRet = _parse(meta.variants?.[v.id]?.retention_info);
      if (_valid(varRet)) return varRet;
    }

    // 2. Fall back to product-level (most common — matches Shopify admin screenshot)
    const prodRet = _parse(meta.metafields?.retention_info);
    if (_valid(prodRet)) return prodRet;

    // retention_info simply not set for this product in Shopify — silent fallback
    // to configProd.quantityOptions. See _logMetafields() boot output for details.
    return null;
  }

  setQuantity(qty) {
    this.selectedQuantity = (qty === this.selectedQuantity) ? null : qty;
    if (!this.selectedQuantity) this.selectedSubProduct = null;
  }

  clearQuantity() {
    this.selectedQuantity     = null;
    this.selectedSubProduct   = null;
    this.selectedDeliveryTime = null;
  }

  setSubProduct(handle) {
    this.selectedSubProduct = (handle === this.selectedSubProduct) ? null : handle;
  }

  setDeliveryTime(key) {
    this.selectedDeliveryTime = (key === this.selectedDeliveryTime) ? null : key;
  }

  clearDeliveryTime() {
    this.selectedDeliveryTime = null;
  }

  // ─── State snapshot (for debugging / external inspection) ────────────────────

  getState() {
    return {
      handle:      this.handle,
      duration:    this.duration,
      dietaryPref: this.dietaryPref,
      variantId:   this.getVariant()?.id ?? null,
      price:       this.getPrice(),
    };
  }

  // ─── Shopify cart payload ─────────────────────────────────────────────────────

  // subProductData: Shopify product JSON for the chosen sub-product (or null).
  // When provided the sub-product's variant is used instead of the parent's.
  toCartItem(duration = this.duration, subProductData = null, variantSku = null, quantityOverride = null) {
    let v;
    if (subProductData) {
      const subVariants = subProductData.variants || [];
      if (variantSku) {
        // Exact SKU match — config explicitly pins the variant to add
        v = subVariants.find(sv => sv.sku === variantSku) || subVariants[0];
      } else {
        // Fall back to duration-label matching then first variant
        const durStr = duration === 1 ? '1 week' : `${duration} week`;
        v = subVariants.find(sv =>
              [sv.option1, sv.option2, sv.title].some(s =>
                s?.toLowerCase().includes(durStr))
            ) || subVariants[0];
      }
    } else {
      v = this.getVariant(duration);
    }
    if (!v) return null;

    // quantityOverride: caller-computed qty (e.g. flour packets needed).
    // Sub-products without an override default to 1 (the multiplier is a
    // per-day pricing factor, not a unit count for the sub-product itself).
    const quantity = quantityOverride ?? (subProductData ? 1 : (this.selectedQuantity || 1));
    const item = { id: v.id, quantity };

    const properties = {};
    if (this.dietaryPref)  properties['Dietary Preference'] = this.dietaryPref;
    if (this.selectedDeliveryTime) {
      const retDt = this.getRetentionInfo()?.delivery_time;
      properties['Delivery Time'] = retDt?.[this.selectedDeliveryTime] || this.selectedDeliveryTime;
    }
    // _Meal is hidden from customers (underscore prefix) but keeps same-variant
    // items from different meal steps as separate Shopify line items
    if (this.stepCategory) properties['_Meal'] = this.stepCategory;
    if (Object.keys(properties).length) item.properties = properties;

    return item;
  }
}
